import type { Property } from '@/types/property';

interface PropertySelectorProps {
  properties: Property[];
  selectedPropertyId: string | null;
  onSelect: (propertyId: string) => void;
  className?: string;
}

/**
 * Compact property switcher for widget headers.
 * - 1 property  → renders nothing
 * - 2 properties → pill toggle (two buttons side by side)
 * - 3+ properties → styled dropdown select
 */
export function PropertySelector({
  properties,
  selectedPropertyId,
  onSelect,
  className,
}: PropertySelectorProps) {
  if (properties.length <= 1) return null;

  if (properties.length === 2) {
    return (
      <div
        className={`flex gap-1 ${className ?? ''}`}
        data-testid="property-selector-toggle"
      >
        {properties.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            aria-pressed={p.id === selectedPropertyId}
            data-testid={`property-toggle-${p.id}`}
            className={`px-2.5 py-1 rounded-full text-body-xs font-medium transition-colors ${
              p.id === selectedPropertyId
                ? 'bg-[var(--color-accent)] text-white'
                : 'bg-[var(--color-elevated)] text-[var(--color-text-muted)] hover:bg-[var(--color-elevated)]'
            }`}
          >
            {p.name}
          </button>
        ))}
      </div>
    );
  }

  return (
    <select
      value={selectedPropertyId ?? ''}
      onChange={(e) => onSelect(e.target.value)}
      data-testid="property-selector-dropdown"
      className={`bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text-primary)] rounded-lg px-3 py-1.5 text-body-xs ${className ?? ''}`}
    >
      {properties.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </select>
  );
}
